export type Audience = 'agents' | 'teams' | 'builders'

export interface CatalogCategory {
  id: string
  label: string
  color: string
}

export interface CatalogWidget {
  id: number
  cat: string
  cats?: string[]
  name: string
  desc: string
  ai?: boolean
}

export interface BuilderWidgetGroup {
  id: string
  label: string
  color: string
  widgets: CatalogWidget[]
}

const CATEGORIES: CatalogCategory[] = [
  { id: 'ai', label: 'AI-Powered', color: '#3AABC4' },
  { id: 'seller', label: 'Seller Leads', color: '#C4873A' },
  { id: 'buyer', label: 'Buyer Tools', color: '#4A9B8E' },
  { id: 'calc', label: 'Calculators', color: '#7B6FAB' },
  { id: 'market', label: 'Market Data', color: '#3A7DC4' },
  { id: 'capture', label: 'Lead Capture', color: '#C44A6A' },
  { id: 'routing', label: 'Lead Routing', color: '#C47A3A' },
  { id: 'recruiting', label: 'Recruiting', color: '#5A9B4A' },
  { id: 'office', label: 'Office Tools', color: '#7B6FAB' },
  { id: 'productivity', label: 'Productivity', color: '#C47A3A' },
  { id: 'builder', label: 'Builder Tools', color: '#A85C32' },
  { id: 'plans', label: 'Plans & Design', color: '#A85C32' },
  { id: 'communities', label: 'Communities & Lots', color: '#5A8F62' },
  { id: 'inventory', label: 'Inventory & Offers', color: '#C46B3A' },
  { id: 'homeowner', label: 'Homeowners', color: '#3A7A8C' },
  { id: 'partners', label: 'Realtors & Trust', color: '#6B5C9E' },
]

const TIER_LIBRARY: Record<string, { id: number; audiences: Audience[]; ai?: boolean }[]> = {
  standard: [
    { id: 23, audiences: ['agents'] },
    { id: 19, audiences: ['agents'] },
    { id: 2, audiences: ['agents'] },
    { id: 66, audiences: ['teams'] },
    { id: 68, audiences: ['teams'] },
    { id: 90, audiences: ['builders'] },
    { id: 91, audiences: ['builders'] },
    { id: 97, audiences: ['builders'] },
    { id: 101, audiences: ['agents', 'teams', 'builders'] },
    { id: 102, audiences: ['agents', 'teams', 'builders'] },
    { id: 104, audiences: ['builders'] },
    { id: 105, audiences: ['agents', 'teams', 'builders'] },
    { id: 106, audiences: ['builders'] },
    { id: 107, audiences: ['builders'] },
    { id: 108, audiences: ['builders'] },
    { id: 109, audiences: ['builders'] },
    { id: 110, audiences: ['agents', 'teams', 'builders'] },
    { id: 111, audiences: ['agents', 'teams', 'builders'] },
    { id: 112, audiences: ['agents', 'teams', 'builders'] },
    { id: 113, audiences: ['builders'] },
    { id: 115, audiences: ['agents', 'teams', 'builders'] },
    { id: 116, audiences: ['builders'] },
    { id: 118, audiences: ['builders'] },
    { id: 119, audiences: ['agents', 'teams', 'builders'] },
    { id: 120, audiences: ['builders'] },
    { id: 121, audiences: ['builders'] },
    { id: 122, audiences: ['agents', 'teams', 'builders'] },
    { id: 123, audiences: ['agents', 'teams', 'builders'] },
    { id: 126, audiences: ['builders'] },
    { id: 128, audiences: ['builders'] },
    { id: 129, audiences: ['builders'] },
    { id: 130, audiences: ['agents', 'teams', 'builders'] },
    { id: 132, audiences: ['agents', 'teams', 'builders'] },
  ],
  premium: [
    { id: 1, audiences: ['agents'], ai: true },
    { id: 47, audiences: ['agents', 'teams'] },
    { id: 36, audiences: ['agents', 'builders'] },
    { id: 78, audiences: ['agents'], ai: true },
    { id: 82, audiences: ['agents', 'builders'], ai: true },
    { id: 140, audiences: ['agents', 'teams', 'builders'], ai: true },
    { id: 85, audiences: ['agents', 'teams', 'builders'] },
    { id: 86, audiences: ['agents', 'teams', 'builders'] },
    { id: 87, audiences: ['agents', 'teams', 'builders'] },
    { id: 88, audiences: ['agents', 'teams', 'builders'] },
    { id: 89, audiences: ['agents', 'teams', 'builders'] },
    { id: 92, audiences: ['builders'] },
    { id: 93, audiences: ['builders'] },
    { id: 94, audiences: ['builders'] },
    { id: 95, audiences: ['builders'] },
    { id: 96, audiences: ['builders'] },
    { id: 98, audiences: ['builders'] },
    { id: 100, audiences: ['agents', 'teams', 'builders'] },
    { id: 103, audiences: ['agents', 'teams', 'builders'] },
    { id: 114, audiences: ['builders'] },
    { id: 125, audiences: ['builders'] },
    { id: 127, audiences: ['builders'] },
    { id: 131, audiences: ['agents', 'teams', 'builders'] },
    { id: 133, audiences: ['builders'], ai: true },
    { id: 134, audiences: ['builders'], ai: true },
    { id: 135, audiences: ['agents', 'teams', 'builders'], ai: true },
    { id: 136, audiences: ['builders'], ai: true },
    { id: 137, audiences: ['builders'], ai: true },
    { id: 138, audiences: ['agents', 'teams', 'builders'], ai: true },
    { id: 139, audiences: ['builders'], ai: true },
  ],
  custom: [
    { id: 64, audiences: ['teams', 'builders'] },
    { id: 65, audiences: ['teams'] },
    { id: 67, audiences: ['teams'] },
    { id: 69, audiences: ['teams'] },
    { id: 141, audiences: ['teams'] },
    { id: 142, audiences: ['teams'] },
    { id: 143, audiences: ['teams'] },
    { id: 144, audiences: ['teams'] },
    { id: 21, audiences: ['builders', 'agents'] },
    { id: 84, audiences: ['agents'], ai: true },
    { id: 99, audiences: ['builders'] },
    { id: 117, audiences: ['builders'] },
    { id: 124, audiences: ['builders'] },
  ],
}

const WIDGETS: CatalogWidget[] = [
  { id: 21, cat: 'buyer', name: 'Property Search / IDX Embed', desc: 'Branded IDX search widget for your site.' },
  { id: 36, cat: 'buyer', name: 'Live Market Pulse Widget', desc: 'Median price, DOM, inventory by ZIP — auto-updating.' },
  { id: 64, cat: 'capture', name: 'Lead Routing Dashboard', desc: 'Auto-assign leads to team members by ZIP or source.' },
  { id: 82, cat: 'buyer', name: 'AI Buyer Matching Tool', desc: 'Natural language search → matched listings.' },
  { id: 85, cat: 'calc', name: 'Monthly Payment Calculator', desc: 'Live rates, taxes, insurance, and incentives — a fully loaded payment calculator.' },
  { id: 86, cat: 'calc', name: 'Affordability Calculator', desc: 'Income and debt inputs reveal real purchase power, with program matches.' },
  { id: 87, cat: 'calc', name: 'Buy vs. Rent Calculator', desc: 'Long-term cost comparison with appreciation and tax-benefit modeling.' },
  { id: 88, cat: 'calc', name: 'Closing Cost Estimator', desc: 'Itemized cash-to-close breakdown by loan type and location.' },
  { id: 89, cat: 'calc', name: 'Lifestyle Total Cost of Ownership', desc: 'The real monthly cost of owning — utilities, maintenance, and insurance included.' },
  { id: 90, cat: 'calc', name: 'Builder Incentive Stack Calculator', desc: 'Stacks every active builder incentive into one total savings number.' },
  { id: 91, cat: 'calc', name: 'Realtor Incentive Calculator', desc: 'Commission and bonus calculator for referring agents, with live alerts.' },
  { id: 92, cat: 'plans', name: 'Floor Plan Matcher Quiz', desc: 'AI-driven quiz matches buyers to the right floor plan, with reasoning.' },
  { id: 93, cat: 'communities', name: 'Community Matcher Quiz', desc: 'Matches buyers to communities by commute, budget, and amenities.' },
  { id: 94, cat: 'communities', name: 'Lot Selector / Recommender', desc: 'Filter and recommend lots by size, view, orientation, and price.' },
  { id: 95, cat: 'plans', name: 'Style / Design Matcher', desc: 'Image-based style quiz matches buyers to elevations and interior packages.' },
  { id: 96, cat: 'plans', name: 'Floor Plan Customizer', desc: 'Toggle structural and design options with live pricing.' },
  { id: 97, cat: 'plans', name: 'Elevation Visualizer', desc: 'Side-by-side elevation comparisons buyers can save and share.' },
  { id: 98, cat: 'plans', name: 'Interior Package Visualizer', desc: 'Room-by-room design package previews with cost differences.' },
  { id: 99, cat: 'communities', name: 'Interactive Lot Map', desc: 'Interactive, filterable map of live lot availability and pricing.' },
  { id: 100, cat: 'capture', name: 'Appointment Scheduler', desc: 'Calendar-synced booking with automatic confirmations and reminders.' },
  { id: 101, cat: 'capture', name: 'Tour Request Widget', desc: 'Pre-qualifies visitors and routes tour requests straight to your team.' },
  { id: 102, cat: 'capture', name: 'Brochure Download Gate', desc: 'Capture a lead before every brochure or spec-sheet download.' },
  { id: 103, cat: 'capture', name: 'Wishlist / Save-for-Later', desc: 'Buyers save favorites across devices and share with family.' },
  { id: 104, cat: 'inventory', name: 'Community Waitlist Widget', desc: 'Early-bird waitlist signup for not-yet-released communities.' },
  { id: 105, cat: 'capture', name: 'Exit-Intent Popup', desc: 'Catches visitors before they leave with a targeted offer.' },
  { id: 106, cat: 'inventory', name: 'Claim Your Incentive', desc: 'One-step incentive claim flow with an urgency countdown.' },
  { id: 107, cat: 'inventory', name: 'Limited Inventory Alert', desc: 'Notify buyers the moment matching inventory becomes available.' },
  { id: 108, cat: 'inventory', name: 'Rate Lock Interest Form', desc: 'Time-boxed rate lock offer with lender handoff and CRM tagging.' },
  { id: 109, cat: 'inventory', name: 'VIP Early Access Signup', desc: 'Pre-sale signup with queue position and a VIP pricing incentive.' },
  { id: 110, cat: 'capture', name: 'Ask a Question Widget', desc: 'Async Q&A routed to the right person, with an auto-acknowledgment.' },
  { id: 111, cat: 'capture', name: 'Send Me the Pricing', desc: 'One-field email capture that auto-delivers your pricing sheet.' },
  { id: 112, cat: 'capture', name: 'Book a Virtual Tour', desc: 'Lightweight scheduling form for virtual tour requests.' },
  { id: 113, cat: 'plans', name: 'Design Center Preview Request', desc: 'Capture design-center interest tied to a specific floor plan.' },
  { id: 114, cat: 'homeowner', name: 'Move-In Cost Estimator', desc: 'Rent-vs-new-home cost comparison gated behind email capture.' },
  { id: 115, cat: 'capture', name: 'Homeowner Stories', desc: 'Video and quote stories from past buyers, each with its own CTA.' },
  { id: 116, cat: 'partners', name: 'Builder Credibility Widget', desc: 'Trust block — years in business, awards, and warranty, at a glance.' },
  { id: 117, cat: 'partners', name: 'Realtor Portal', desc: 'Referral pipeline, marketing library, and messaging for partner agents.' },
  { id: 118, cat: 'homeowner', name: 'Construction Timeline Visualizer', desc: 'Milestone-by-milestone build progress with estimated dates.' },
  { id: 119, cat: 'buyer', name: 'HOA Cost Explainer', desc: 'Itemized HOA breakdown with amenity valuation and comparisons.' },
  { id: 120, cat: 'homeowner', name: 'Warranty Coverage Explainer', desc: 'Year-by-year coverage detail — what\'s covered, what\'s not.' },
  { id: 121, cat: 'homeowner', name: 'Move-In Date Calculator', desc: 'Backlog-aware move-in date estimate, specific to the selected plan.' },
  { id: 122, cat: 'buyer', name: 'First-Time Buyer Program Eligibility', desc: 'Detects federal, state, and local first-time buyer programs automatically.' },
  { id: 123, cat: 'buyer', name: 'Relocation Calculator', desc: 'State-to-state cost-of-living, tax, and climate comparison.' },
  { id: 124, cat: 'homeowner', name: 'Customer Portal', desc: 'Construction progress, design selections, and documents in one login.' },
  { id: 125, cat: 'homeowner', name: 'Warranty Claim Intake', desc: 'Photo-based warranty claims with AI categorization and routing.' },
  { id: 126, cat: 'homeowner', name: 'Move-In Checklist', desc: 'Task-by-task move-in checklist with reminders.' },
  { id: 127, cat: 'homeowner', name: 'Homeowner Resource Hub', desc: 'AI-searchable resource hub with maintenance reminders and video guides.' },
  { id: 128, cat: 'inventory', name: 'Inventory Spotlight Widget', desc: 'Live spec-home feed with urgency cues and incentive callouts.' },
  { id: 129, cat: 'communities', name: 'Community Comparison Widget', desc: 'Side-by-side community comparison by price and amenities.' },
  { id: 130, cat: 'capture', name: 'Review / Testimonial Carousel', desc: 'Google and Facebook reviews, filterable by community.' },
  { id: 131, cat: 'capture', name: 'Live Video Walk Request', desc: 'On-demand or scheduled live video walkthroughs.' },
  { id: 132, cat: 'capture', name: 'Promotional Banner', desc: 'Configurable promo banner with countdowns and geo-targeting.' },
  { id: 133, cat: 'communities', name: 'What Can I Build Here?', desc: 'AI matches budget and ZIP to buildable floor plans, with reasoning.' },
  { id: 134, cat: 'partners', name: 'New Home vs. Resale Quiz', desc: 'Short quiz makes the case for new construction, personalized by AI.' },
  { id: 135, cat: 'buyer', name: 'Is Now a Good Time to Buy Quiz', desc: 'AI-scored readiness quiz routes leads by how ready they are.' },
  { id: 136, cat: 'plans', name: 'Find My Floor Plan', desc: '3-question quiz returns AI-ranked floor plan matches.' },
  { id: 137, cat: 'communities', name: 'Right Community Finder', desc: 'Lifestyle-priority picker returns AI-ranked community matches.' },
  { id: 138, cat: 'capture', name: 'Smart AI Contact Form', desc: 'Conversational form that auto-qualifies and routes every lead.' },
  { id: 139, cat: 'partners', name: 'AI Buyer Q&A Assistant', desc: '24/7 AI assistant trained on your floor plans, pricing, and FAQs.' },
  { id: 140, cat: 'buyer', name: 'Home Buying Readiness Assessment', desc: 'Stage-detection quiz with personalized next steps.' },
]

const CATEGORY_ORDER = ['plans', 'communities', 'inventory', 'calc', 'capture', 'buyer', 'homeowner', 'partners']
const PRIORITY = [99, 117, 124, 92, 93, 94, 133, 136, 137, 139, 85, 86]

function builderMeta() {
  const ids = new Set<number>()
  const ai = new Set<number>()
  for (const items of Object.values(TIER_LIBRARY)) {
    for (const item of items) {
      if (!item.audiences.includes('builders')) continue
      ids.add(item.id)
      if (item.ai) ai.add(item.id)
    }
  }
  return { ids, ai }
}

export function getBuilderWidgetCount(): number {
  return builderMeta().ids.size
}

export type WidgetTier = 'standard' | 'premium' | 'custom'

export const TIER_MONTHLY: Record<WidgetTier, number> = {
  standard: 19,
  premium: 49,
  custom: 0,
}

export function getWidgetTier(id: number): WidgetTier | null {
  for (const tier of ['standard', 'premium', 'custom'] as const) {
    if (TIER_LIBRARY[tier].some(item => item.id === id && item.audiences.includes('builders'))) {
      return tier
    }
  }
  return null
}

export function getBuilderWidgetById(id: number): CatalogWidget | undefined {
  const { ai } = builderMeta()
  const w = WIDGETS.find(x => x.id === id)
  return w ? { ...w, ai: ai.has(w.id) } : undefined
}

export function getBuilderWidgetGroups(): BuilderWidgetGroup[] {
  const { ids, ai } = builderMeta()
  const byId = new Map(WIDGETS.map(w => [w.id, w]))
  const widgets = [...ids]
    .map(id => byId.get(id))
    .filter((w): w is CatalogWidget => !!w)
    .map(w => ({ ...w, ai: ai.has(w.id) }))

  const pri = new Map(PRIORITY.map((id, i) => [id, i]))
  const sortWidgets = (list: CatalogWidget[]) =>
    [...list].sort((a, b) => (pri.get(a.id) ?? 500) - (pri.get(b.id) ?? 500) || a.name.localeCompare(b.name))

  const catMap = new Map(CATEGORIES.map(c => [c.id, c]))
  const grouped: BuilderWidgetGroup[] = []
  const used = new Set<number>()

  for (const catId of CATEGORY_ORDER) {
    const cat = catMap.get(catId)
    if (!cat) continue
    const list = sortWidgets(widgets.filter(w => w.cat === catId || w.cats?.includes(catId)))
    if (!list.length) continue
    grouped.push({ ...cat, widgets: list })
    list.forEach(w => used.add(w.id))
  }

  const leftover = widgets.filter(w => !used.has(w.id))
  if (leftover.length) {
    const extra = new Map<string, CatalogWidget[]>()
    for (const w of leftover) {
      extra.set(w.cat, [...(extra.get(w.cat) ?? []), w])
    }
    for (const [catId, list] of extra) {
      const cat = catMap.get(catId)
      if (!cat) continue
      grouped.push({ ...cat, widgets: sortWidgets(list) })
    }
  }

  return grouped
}
